import { 
  Refresh, 
  Download, 
  Link, 
  SwapHoriz, 
  Code,
  Timeline,
  Insights,
  Search
} from '@mui/icons-material'
import { 
  Button, 
  Box, 
  Typography, 
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  Divider,
  Paper,
  TextField,
  InputAdornment
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useState, useEffect } from 'react'

// Types for mapping data (according to spec)
type TriggerSource = {
  id: string
  name: string
  hubEvents: string[]
}

type HubEvent = {
  id: string
  name: string
  triggerSources: string[]
}

type Props = {
  isLoading?: boolean
  onRefresh?: () => void
  onDownloadCsv?: () => void
}

// endpoint to map
//  /api/v1/notifications/triggers
export default function MappingTab({ isLoading = false, onRefresh, onDownloadCsv }: Props) {
  const [triggerSourcesData, setTriggerSourcesData] = useState<TriggerSource[]>([])
  const [hubEventsData, setHubEventsData] = useState<HubEvent[]>([])
  const [searchTerm, setSearchTerm] = useState('')

  // TODO: Replace with actual API call to GET /api/v1/notifications/triggers/sources
  useEffect(() => {
    // Mock data for demonstration
    setTriggerSourcesData([
      { id: '1', name: 'TASK_CREATED', hubEvents: ['task.created', 'workflow.task_created'] },
      { id: '2', name: 'TASK_COMPLETED', hubEvents: ['task.completed', 'workflow.task_completed'] }
    ])

    setHubEventsData([
      { id: '1', name: 'task.created', triggerSources: ['TASK_CREATED'] },
      { id: '2', name: 'task.completed', triggerSources: ['TASK_COMPLETED'] }
    ])
  }, [])

  // Filter data based on search term
  const filteredTriggerSources = triggerSourcesData.filter(trigger => 
    trigger.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trigger.hubEvents.some(event => event.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const filteredHubEvents = hubEventsData.filter(event => 
    event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.triggerSources.some(trigger => trigger.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const triggerSourceColumns: GridColDef<TriggerSource>[] = [
    {
      field: 'name',
      headerName: 'Fonte do Trigger',
      flex: 1,
      minWidth: 200,
      renderCell: params => (
        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Gatilho do sistema
          </Typography>
        </Box>
      )
    },
    {
      field: 'hubEvents',
      headerName: 'Eventos Associados (Hub Events)',
      flex: 2,
      minWidth: 400,
      renderCell: params => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 1 }}>
          {params.value?.map((event: string, index: number) => (
            <Chip
              key={index}
              label={event}
              size="small"
              variant="outlined"
              color="primary"
              icon={<Timeline />}
              sx={{ 
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                '& .MuiChip-icon': { fontSize: '0.875rem' }
              }}
            />
          ))}
        </Box>
      )
    }
  ]

  const hubEventColumns: GridColDef<HubEvent>[] = [
    {
      field: 'name',
      headerName: 'Hub Event',
      flex: 1,
      minWidth: 200,
      renderCell: params => (
        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Evento do hub
          </Typography>
        </Box>
      )
    },
    {
      field: 'triggerSources',
      headerName: 'Triggers Associados (Sources)',
      flex: 2,
      minWidth: 400,
      renderCell: params => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 1 }}>
          {params.value?.map((trigger: string, index: number) => (
            <Chip
              key={index}
              label={trigger}
              size="small"
              variant="outlined"
              color="warning"
              icon={<Code />}
              sx={{ 
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                '& .MuiChip-icon': { fontSize: '0.875rem' }
              }}
            />
          ))}
        </Box>
      )
    }
  ]

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center" 
        sx={{ mb: 4 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
              <Link />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Mapeamento de Eventos
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  TRIGGER_SOURCE
                </Typography>
                <SwapHoriz color="action" />
                <Typography variant="body2" color="text.secondary">
                  HUB_EVENT
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Box>
        
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={onRefresh}
            disabled={isLoading}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={onDownloadCsv}
            disabled={isLoading}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Download CSV
          </Button>
        </Stack>
      </Stack>

      {/* Search Section */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <TextField
            fullWidth
            placeholder="Buscar por trigger source, hub event ou mapeamentos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
        <Card variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'warning.light' }}>
                <Code />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Fontes de Trigger
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {filteredTriggerSources.length}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        
        <Card variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'success.light' }}>
                <Timeline />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Eventos do Hub
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {filteredHubEvents.length}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'info.light' }}>
                <Insights />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Total de Mapeamentos
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {filteredTriggerSources.reduce((acc, ts) => acc + ts.hubEvents.length, 0)}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Table 1: Gatilhos por Evento */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'warning.main' }}>
                <Code />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Tabela 1: Gatilhos por Evento
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Mapeamento de Trigger Sources para Hub Events
                </Typography>
              </Box>
            </Stack>
          </Box>
          <DataGrid
            rows={filteredTriggerSources}
            columns={triggerSourceColumns}
            loading={isLoading}
            getRowId={row => row.id}
            disableRowSelectionOnClick
            rowHeight={60}
            sx={{ 
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'grey.50',
                borderBottom: '2px solid',
                borderColor: 'divider'
              },
              '& .MuiDataGrid-cell': {
                borderColor: 'grey.100',
                py: 1
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }
            }}
            hideFooterPagination
            hideFooterSelectedRowCount
          />
        </CardContent>
      </Card>

      {/* Table 2: Eventos por Gatilho */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'success.main' }}>
                <Timeline />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Tabela 2: Eventos por Gatilho
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Mapeamento de Hub Events para Trigger Sources
                </Typography>
              </Box>
            </Stack>
          </Box>
          <DataGrid
            rows={filteredHubEvents}
            columns={hubEventColumns}
            loading={isLoading}
            getRowId={row => row.id}
            disableRowSelectionOnClick
            rowHeight={60}
            sx={{ 
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'grey.50',
                borderBottom: '2px solid',
                borderColor: 'divider'
              },
              '& .MuiDataGrid-cell': {
                borderColor: 'grey.100',
                py: 1
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }
            }}
            hideFooterPagination
            hideFooterSelectedRowCount
          />
        </CardContent>
      </Card>
    </Box>
  )
}
